/// <amd-module name="Test/AssociateToTestCase/Components/AssociateToTestCaseDialog" />
import * as React from "react";
import { AssociateToTestCaseActionCreator } from "Actions/AssociateToTestCaseActionCreator";
import { ITestResultTreeData } from "Test/common-util/Contracts/ViewContracts";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { AssociateToTestCaseActionHub } from "Test/AssociateToTestCase/Actions/AssociateToTestCaseActionHub";
import * as Resources from "Test/AssociateToTestCase/Resources";
import { IAssociateToTestCaseState, AssociateToTestCaseStore } from "Test/AssociateToTestCase/Store/AssociateToTestCaseStore";
import { IVssPageContext } from "VSS/Platform/Context";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { WorkItem } from 'Wit/Clients/Wit/Contracts/WorkItemTracking';
import { FormItem } from "VSSUI/FormItem";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { ObservableValue } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { Status, StatusSize, Statuses } from "VSSUI/Status";

export interface IAssociateToTestCaseDialogProps {
    testRunId: number;
    testResultId: number;
    associateToTestCaseResult: ITestResultTreeData;
}

export class AssociateToTestCaseDialog extends VssComponent<IAssociateToTestCaseDialogProps, IAssociateToTestCaseState> {
    private _store: AssociateToTestCaseStore;
    private _actionCreator: AssociateToTestCaseActionCreator;
    private _actionHub: AssociateToTestCaseActionHub;
    private _pageContext: IVssPageContext;
    private _testCaseId = new ObservableValue<string>("");

    public constructor(props: IAssociateToTestCaseDialogProps, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        this._actionHub = new AssociateToTestCaseActionHub();
        this._store = new AssociateToTestCaseStore(this._pageContext, this._actionHub);
        this._actionCreator = new AssociateToTestCaseActionCreator(this._pageContext, this._actionHub);
        this.state = this._store.getState();
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();
        this._store.addChangedListener(this._handleStoreChange);
        await this._actionCreator.initialize(this.props.testRunId, this.props.testResultId);
    }

    public componentWillUnmount(): void {
        this._store.dispose();
        this._store.removeChangedListener(this._handleStoreChange);
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        return (
            <ScreenSizeObserver>
                {
                    (screenSizeProps: { screenSize: ScreenSize }) => {
                        return this._renderCustom(screenSizeProps.screenSize);
                    }
                }
            </ScreenSizeObserver>
        );
    }

    private _renderCustom(screenSize: ScreenSize): JSX.Element {
        let calloutContentClassName: string = this._getCalloutClassname(screenSize);
        return (
            <div>
                {this.state.showDialog && (
                    <Dialog
                        className={calloutContentClassName}
                        titleProps={{ text: this.getTitleText() }}
                        footerButtonProps={[
                            {
                                text: Resources.SaveButtonText,
                                primary: true,
                                disabled: !!this.state.errorMessage || !(this.state.isAssociated && !this.state.associationSaved),
                                onClick: this._onClickSave
                            },
                            {
                                text: Resources.CloseButtonText,
                                onClick: this._onDismiss
                            }
                        ]}
                        onDismiss={this._onDismiss}
                    >
                        {this.state.loading && this._getLoadingSpinner()}
                        {this.state.errorMessage && this.state.errorMessage !== "" && this._getErrorMessageElement()}
                        {!this.state.loading && !this.state.errorMessage && (
                            <div className={"associatetotestcase-content"}>
                                <span className={"content-container"}>
                                    {this._getInputTestCaseId()}
                                    {this._getFailureMessages()}
                                    {this._getExistingAssociations(screenSize, this.state.isAssociated)}
                                </span>
                            </div>
                        )}
                        {!this.state.loading && this._getSuccessMessage()}
                    </Dialog>
                )}
            </div>
        );
    }

    private _getSuccessMessage(): JSX.Element {
        return (
            <div className="associatetotestcase-failure">
                <p />
                {!this.state.associationsExist && Resources.NoExistingAssociations}
                {this.state.associationSaved && (
                    <div className="flex-row" style={{ flexWrap: "wrap" }}>
                        <Status
                            {...Statuses.Success}
                            key="success"
                            size={StatusSize.m}
                            className="status-example flex-self-center"
                        />
                        <span>{Resources.AssociationSaved}</span>
                    </div>
                )}
            </div>
        );
    }

    private _getFailureMessages(): JSX.Element {
        return (
            <div className="associatetotestcase-success">
                <p />
                {!this.state.isAssociated && this.state.associateError != "" && (
                    <div className="flex-row" style={{ flexWrap: "wrap" }}>
                        <Status
                            {...Statuses.Failed}
                            key="failed"
                            size={StatusSize.m}
                            className="status-example flex-self-center "
                        />
                        {this.state.associateError == "Failed to get TestCaseId" && <span>{Resources.InvalidTestCaseId}</span>}
                        {this.state.associateError == "Association Already Exists" && <span>{Resources.AssociationAlreadyExists}</span>}
                        {this.state.associateError == "WorkItem is not TestCase" && <span>{Resources.NotATestCase}</span>}
                    </div>
                )}
            </div>
        );
    }

    private _getCalloutClassname(screenSize: ScreenSize): string {
        let calloutContentClassName: string = "associatetotestcase-dialog";

        if (screenSize < ScreenSize.small) {
            calloutContentClassName = calloutContentClassName + " associatetotestcase-dialog-sm";
        }
        return calloutContentClassName;
    }

    private _getInputTestCaseId(): JSX.Element {
        return (
            <div className="associatetotestcase-inputtestcaseid">
                {Resources.AssociateToTestCaseMessageBar}
                <div className="associatetotestcase-inputid flex-row">
                    <FormItem>
                        <TextField
                            value={this._testCaseId}
                            onChange={(e, newTestCaseIdValue) => (
                                this._testCaseId.value = newTestCaseIdValue.replace(/[^0-9]/g, "") // Remove non-numeric characters
                            )}
                            disabled={!!this.state.errorMessage || this.state.isAssociated}
                            width={TextFieldWidth.standard}
                            placeholder={Resources.TestCaseIdInputWatermark}
                        />
                    </FormItem>
                    <Button
                        className="assoc-button"
                        text={Resources.AddAssociation}
                        onClick={() => {
                            this._addAssociation();
                        }}
                        disabled={this.state.isAssociated}
                    />
                </div>
            </div>
        );
    }

    private _getLoadingSpinner(): JSX.Element {
        return (
            <Spinner className={"associatetotestcase-spinner"} size={SpinnerSize.medium} />
        );
    }

    private _addAssociation() {
        if (this._testCaseId.value != "") {
            this._actionCreator.addAssociateTestCase(this._testCaseId.value);
        }
    }

    private _fetchWorkItemGrid(screenSize: ScreenSize, isAssociated: boolean): JSX.Element {
        const tableRows = [];
        const tableHeaders = [];
        let workItem: WorkItem[] = this.state.workItems?.slice();

        // Extract table headers from the first object in the array
        tableHeaders.push(<th key="ID">ID</th>);
        tableHeaders.push(<th key="Work Item Type">Work Item Type</th>);
        tableHeaders.push(<th key="Title">Title</th>);

        if (workItem != null && workItem.length > 0) {
            for (let i = 0; i < workItem.length; i++) {
                const rowData: WorkItem = workItem[i];
                const cells = [];

                for (const key in rowData) {
                    {
                        if (key === "id") {
                            cells.push(<td style={{ textAlign: 'center'}} key={key}>{rowData.id}</td>);
                        }
                        else if (key === "fields") {
                            cells.push(<td style={{ textAlign: 'center'}} key={key + "WIType"}>{rowData.fields["System.WorkItemType"]}</td>);
                            cells.push(<td style={{ textAlign: 'center'}} key={key + "Title"}>{rowData.fields["System.Title"]}</td>);
                        }
                    }
                }

                tableRows.push(<tr key={i}>{cells}</tr>);
            }
        }

        //If associated, append the input TestCaseId to existing associations
        const newCell = [];
        if (isAssociated) {
            newCell.push(<td style={{ textAlign: 'center'}} key={"id"}>{this._testCaseId.value}</td>);
            newCell.push(<td style={{ textAlign: 'center'}} key={"fieldsWIType"}>{this.state.associatedTestCase.fields["System.WorkItemType"]}</td>);
            newCell.push(<td style={{ textAlign: 'center'}} key={"fieldsTitle"}>{this.state.associatedTestCase.fields["System.Title"]}</td>);
        }
        tableRows.push(<tr key={"-1"}>{newCell}</tr>);

        return (
            <div style={{ overflowY: 'scroll', maxHeight: '150px' }} tabIndex={0}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f2f2f2' }}>
                        <tr>{tableHeaders}</tr>
                    </thead>
                    <tbody>{tableRows}</tbody>
                </table>
            </div>
        );
    }

    private _getExistingAssociations(screenSize: ScreenSize, isAssociated: boolean): JSX.Element {
        return (
            <div>
                <p /><p />
                {Resources.ExistingAssociations}
                {this._fetchWorkItemGrid(screenSize, isAssociated)}
            </div>
        );
    }

    private _onClickSave = () => {
        Telemetry.publishEvent(this.context.pageContext, this._pageContext, TelemetryConstants.featureTestTab_AssociateToTestCaseClicked, {
            count: 1,
            RunId: this.props.testRunId,
            ResultId: this.props.testResultId
        });

        this._actionCreator.associateToTestCase(this._testCaseId.value, this.state.testResults.automatedTestName, this.state.testResults.automatedTestStorage);
        this.setState({ associationSaved: true });
    };

    private _onDismiss = () => {
        Telemetry.publishEvent(this.context.pageContext, this._pageContext, TelemetryConstants.featureTestTab_AssociateToTestCaseDismissClicked, {
            count: 1
        });

        this._actionHub.onDismiss.invoke(undefined);
    };

    private getTitleText(): string {
        if (this.state.loading) {
            return "";
        }

        return Resources.AssociateToTestCase + " - " + this.state.testResults.testCaseTitle;
    }

    private _getErrorMessageElement(): JSX.Element {
        Telemetry.publishEvent(this.context.pageContext, this._pageContext, TelemetryConstants.featureTestTab_AssociateToTestCaseError, {
            error: this.state.errorMessage
        });

        return (
            <div className="associatetotestcase-message-error">
                <MessageCard severity={MessageCardSeverity.Error}>
                    <div className="associatetotestcase-errormessage-bar">{this.state.errorMessage}</div>
                </MessageCard>
            </div>
        );
    }

    private _handleStoreChange = (): void => {
        this.setState(this._store.getState());
    };
}

Components.add("test-associatetotestcase", AssociateToTestCaseDialog);